import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ColId =
  | "qh"
  | "interval"
  | "status"
  | "daPriceEur"
  | "daBaseMw"
  | "qhBuyMw"
  | "qhBuyVwap"
  | "qhSellMw"
  | "qhSellVwap"
  | "phBuyMw"
  | "phBuyVwap"
  | "phSellMw"
  | "phSellVwap"
  | "idTotalMw"
  | "idVwapEur"
  | "netMw"
  | "netVwapEur"
  | "pnlEur"
  | "bmNetMw"
  | "bmNetMwh"
  | "bmPriceSource"
  | "bmPositivePriceEur"
  | "bmNegativePriceEur"
  | "bmAppliedPriceEur"
  | "bmPositionPriceEur"
  | "bmSpreadEur"
  | "bmSettlementEur"
  | "bmPnlEur"
  | "bmDataStatus";

export const DEFAULT_ORDER: ColId[] = [
  "qh",
  "interval",
  "status",
  "daPriceEur",
  "daBaseMw",
  "qhBuyMw",
  "qhBuyVwap",
  "qhSellMw",
  "qhSellVwap",
  "phBuyMw",
  "phBuyVwap",
  "phSellMw",
  "phSellVwap",
  "idTotalMw",
  "idVwapEur",
  "netMw",
  "netVwapEur",
  "pnlEur",
  "bmNetMw",
  "bmNetMwh",
  "bmPriceSource",
  "bmAppliedPriceEur",
  "bmPositionPriceEur",
  "bmSpreadEur",
  "bmPnlEur",
  "bmDataStatus",
  "bmSettlementEur",
  "bmPositivePriceEur",
  "bmNegativePriceEur",
];

interface GridColumnsState {
  order: ColId[];
  collapsedGroups: string[];
  reorder(from: ColId, to: ColId, mode: "before" | "after"): void;
  toggleGroup(group: string): void;
  reset(): void;
}

function normalise(current: ColId[]): ColId[] {
  const seen = new Set<ColId>();
  const out: ColId[] = [];
  for (const id of current) {
    if (DEFAULT_ORDER.includes(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  for (const id of DEFAULT_ORDER) if (!seen.has(id)) out.push(id);
  return out;
}

export const useGridColumns = create<GridColumnsState>()(
  persist(
    (set) => ({
      order: [...DEFAULT_ORDER],
      collapsedGroups: [],
      reorder: (from, to, mode) =>
        set((s) => {
          if (from === to) return s;
          const next = s.order.filter((x) => x !== from);
          const targetIdx = next.indexOf(to);
          if (targetIdx === -1) return s;
          const insertIdx = mode === "before" ? targetIdx : targetIdx + 1;
          next.splice(insertIdx, 0, from);
          return { order: next };
        }),
      toggleGroup: (group) =>
        set((s) => {
          const set_ = new Set(s.collapsedGroups);
          if (set_.has(group)) set_.delete(group);
          else set_.add(group);
          return { collapsedGroups: [...set_] };
        }),
      reset: () => set({ order: [...DEFAULT_ORDER], collapsedGroups: [] }),
    }),
    {
      name: "chro-grid-columns-v2",
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.order = normalise(state.order);
          if (!Array.isArray(state.collapsedGroups)) state.collapsedGroups = [];
        }
      },
    }
  )
);
